import React, { type FC } from 'react'
import { Box, Link, Stack, type SvgIconProps, type SxProps, type Theme } from '@mui/material'

import { Translation } from '../../../domain'

import { DocumentationType, LogoutReason } from '../../../application'

import { DocumentationFilledIcon, LogoutIcon } from '../common'
import { DocumentationLink } from '../Documentation'
import { useTranslator } from '../Internationalization'
import { AbsoluteRouteOnlyPath, useRouting } from '../routing'
import { useAppUsecase } from '../State'
import { insetFocusOutline } from '../theme'
import { TOP_NAVIGATION_SECTIONS } from './Config'
import { useNavigationFormatter } from './Hooks'
import { NavigationPermissionedLink } from './Link'
import { SessionCountdown } from './SessionCountdown'
import { TopBar } from './TopBar'

/**
 * There are 2 types of links:
 * - text links (nav items with icon and text, on the left)
 * - icon links (nav items with icon only, on the right)
 */

/**
 * Left nav items (text links): a 36px row holding the icon and label, and 4px below it a 2px
 * line that turns blue under the selected tab. Hovering fills the row, not the whole tab.
 */
const TAB_ROW_HEIGHT = 36
const TAB_LINE_HEIGHT = 2
const ICON_SIZE = 20
const TAB_ROW_CLASS = 'navigation-tab-row'

const linkTextLinkStyle: SxProps<Theme> = (theme) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    color: 'grey.700',
    whiteSpace: 'nowrap',
    ...theme.typography.body2,
    fontWeight: 500,
    [`&:hover .${TAB_ROW_CLASS}`]: { backgroundColor: 'grey.200' },
    // Like hover, the keyboard focus outline sits on the row, the same 2px inset blue outline as the side bar
    '&:focus-visible': { outline: 'none' },
    [`&:focus-visible .${TAB_ROW_CLASS}`]: insetFocusOutline(theme),
})
const selectedTextLinkStyle: SxProps<Theme> = { color: 'primary.700' }
const tabRowStyle: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    height: TAB_ROW_HEIGHT,
    boxSizing: 'border-box',
    p: 2,
    borderRadius: 1,
}
/** A 4px corner on a 2px line gives it fully rounded ends */
const tabLineStyle: SxProps<Theme> = { height: TAB_LINE_HEIGHT, borderRadius: 1, backgroundColor: 'transparent' }
const selectedTabLineStyle: SxProps<Theme> = { backgroundColor: 'primary.700' }
const iconTextLinkStyle: SxProps<Theme> = { width: ICON_SIZE, height: ICON_SIZE, flexShrink: 0, color: 'grey.500' }
const selectedIconTextLinkStyle: SxProps<Theme> = { color: 'inherit' }

/** The tabs sit at the bottom of the bar, the blue line 1px above the page panel */
const tabsStyle: SxProps<Theme> = { alignSelf: 'stretch', alignItems: 'flex-end', pb: 0.25 }

// right nav items styles (icon links)
const linkIconLinkStyle: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    p: 2,
    cursor: 'pointer',
    color: 'grey.500',
    '&:hover': { color: 'grey.700' },
}
const selectedIconLinkStyle: SxProps<Theme> = { color: 'primary.700' }
const iconIconLinkStyle: SxProps<Theme> = {
    width: ICON_SIZE,
    height: ICON_SIZE,
    color: 'inherit',
}
const selectedIconIconLinkStyle: SxProps<Theme> = { color: 'primary.700' }

const ExternalLink: FC<Readonly<{ onClick: VoidFunction } | { documentation: DocumentationType }> & { icon: FC<SvgIconProps>, title: Translation }> = ({
    icon: Icon,
    title,
    ...props
}) => {
    const translator = useTranslator()
    const linkTitle = translator.formatTranslation(title)

    if ('documentation' in props) {
        return (
            <DocumentationLink {...props} type={props.documentation} title={linkTitle} aria-label={linkTitle} sx={linkIconLinkStyle}>
                <Icon sx={iconIconLinkStyle} />
            </DocumentationLink>
        )
    }

    return (
        // A real button, so it can be reached and used with the keyboard: a link without an address can't be
        <Link {...props} component="button" type="button" onClick={props.onClick} title={linkTitle} aria-label={linkTitle} sx={linkIconLinkStyle}>
            <Icon sx={iconIconLinkStyle} />
        </Link>
    )
}

const NavigationLink: FC<Readonly<{ route: AbsoluteRouteOnlyPath }>> = ({ route }) => {
    const routing = useRouting()
    const formatter = useNavigationFormatter()
    const selected = routing.isAtRoute(route)
    const Icon = formatter.formatIcon(route)

    return (
        <NavigationPermissionedLink path={route} aria-current={selected ? 'true' : undefined} sx={[linkTextLinkStyle, selected && selectedTextLinkStyle]}>
            <Box component="span" className={TAB_ROW_CLASS} sx={tabRowStyle}>
                <Icon sx={[iconTextLinkStyle, selected && selectedIconTextLinkStyle]} />
                {formatter.formatTitle(route)}
            </Box>
            <Box component="span" sx={[tabLineStyle, selected && selectedTabLineStyle]} />
        </NavigationPermissionedLink>
    )
}

const NavigationIconLink: typeof NavigationLink = ({ route }) => {
    const routing = useRouting()
    const formatter = useNavigationFormatter()
    const selected = routing.isAtRoute(route)
    const Icon = formatter.formatIcon(route)

    return (
        <NavigationPermissionedLink
            path={route}
            aria-label={formatter.formatTitle(route)}
            aria-current={selected ? 'page' : undefined}
            sx={[linkIconLinkStyle, selected && selectedIconLinkStyle]}
        >
            <Icon sx={[iconIconLinkStyle, selected && selectedIconIconLinkStyle]} />
        </NavigationPermissionedLink>
    )
}

const toolbarContentStyle: SxProps<Theme> = { width: '100%', height: '100%', justifyContent: 'space-between' }
const linksWrapperStyle: SxProps<Theme> = { flex: 1, px: 4 }
const emptyBoxStyle: SxProps<Theme> = { mx: 'auto' }

export const TopNavigation: FC = () => {
    const logoutUsecase = useAppUsecase('logoutUsecase')
    const translator = useTranslator()

    return (
        <TopBar>
            <Stack direction="row" sx={toolbarContentStyle} data-testid="navigation-bar">
                <Stack direction="row" sx={linksWrapperStyle} data-testid="navigation-list">
                    <Stack
                        component="nav"
                        aria-label={translator.formatTranslation(Translation.NAVIGATION_TOP_LABEL)}
                        direction="row"
                        gap={5}
                        sx={tabsStyle}
                    >
                        {TOP_NAVIGATION_SECTIONS.map((route) => <NavigationLink key={route} route={route} />)}
                    </Stack>

                    <Box sx={emptyBoxStyle} />

                    <SessionCountdown />

                    <Stack direction="row" gap={1.5}>
                        <ExternalLink
                            data-testid="documentation-top-link"
                            icon={DocumentationFilledIcon}
                            documentation={DocumentationType.CYBUS_DOCS}
                            title={Translation.DOCUMENTATION}
                        />
                        <NavigationIconLink route={AbsoluteRouteOnlyPath.SETTINGS} />
                        <ExternalLink
                            data-testid="logout-top-link"
                            icon={LogoutIcon}
                            onClick={() => logoutUsecase.logout(LogoutReason.MANUAL_LOGOUT)}
                            title={Translation.LOGOUT}
                        />
                    </Stack>
                </Stack>
            </Stack>
        </TopBar>
    )
}
